import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Heart, ArrowLeft } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { CarCard } from '@/components/CarCard';
import { useFavorites } from '@/context/FavoritesContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

export default function FavoritesPage() {
  const { favorites, loading, count } = useFavorites();
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{language === 'de' ? 'Merkliste | ' : 'Wishlist | '}{settings?.site_name || 'Tim Automobile'}</title>
        <meta name="description" content={language === 'de' 
          ? 'Ihre persönliche Merkliste mit ausgewählten Premium-Fahrzeugen. Behalten Sie Ihre Favoriten im Blick.'
          : 'Your personal wishlist with selected premium vehicles. Keep track of your favorites.'
        } />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Link 
            to="/cars" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft size={16} />
            <span className="text-sm uppercase tracking-wider">{t('back_to_inventory')}</span>
          </Link>
          <div className="flex items-center gap-3">
            <Heart className="text-primary" size={28} fill="#D4AF37" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light">
              {t('favorites_title')}
            </h1>
          </div>
          <p className="text-muted-foreground mt-4 max-w-2xl">
            {t('favorites_description')}
          </p>
        </div>
      </section>

      {/* Favorites Grid */}
      <section className="pb-24" data-testid="favorites-grid">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-muted w-1/4" />
                    <div className="h-6 bg-muted w-1/2" />
                    <div className="h-4 bg-muted w-3/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-20 bg-card border border-border">
              <Heart size={64} className="mx-auto text-muted mb-6" />
              <h3 className="font-heading text-2xl mb-2">{t('favorites_empty')}</h3>
              <p className="text-muted-foreground mb-8">
                {t('favorites_empty_hint')}
              </p>
              <Link to="/cars" className="btn-primary">
                {t('favorites_browse')}
              </Link>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground mb-8">
                {count} {count === 1 ? t('favorites_count_singular') : t('favorites_count_plural')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {favorites.map((car, index) => (
                  <CarCard key={car.id} car={car} index={index} showFavorite />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
